import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { SectionHead } from "./Primitives";
import { Illu } from "./Illustrations";
import { Send, X, Loader2, ArrowRight, Instagram } from "lucide-react";

const CHANNELS = [
  ["email", "E-Mail"],
  ["phone", "Telefon"],
  ["whatsapp", "WhatsApp"],
];

const TYPES = [
  ["dystopisch", "Dystopisches Projekt"],
  ["motiv", "Verfügbares Motiv"],
  ["custom", "Eigene Tattooidee"],
];

const empty = {
  name: "",
  email: "",
  phone: "",
  contact_channel: "email",
  project_type: "custom",
  description: "",
  placement: "",
  size: "",
  consent: false,
  marketing_consent: false,
  honeypot: "",
};

export const InquiryForm = ({ texts, selectedMotif, onClearMotif, instagram }) => {
  const [form, setForm] = useState(empty);
  const [files, setFiles] = useState([]);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const nameRef = useRef(null);

  useEffect(() => {
    if (selectedMotif) {
      setForm((f) => ({ ...f, project_type: "motiv" }));
      setState("idle");
    }
  }, [selectedMotif]);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.consent) {
      setError("Bitte bestätige die Verarbeitung deiner Angaben.");
      return;
    }
    setState("sending");
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, typeof v === "boolean" ? String(v) : v));
    fd.append("motif_id", selectedMotif?.id || "");
    fd.append("motif_title", selectedMotif?.title || "");
    const params = new URLSearchParams(window.location.search);
    fd.append("source", params.get("utm_source") || (document.referrer.includes("instagram") ? "instagram" : "direct"));
    fd.append("campaign", params.get("utm_campaign") || "");
    fd.append("referral_code", params.get("ref") || "");
    files.forEach((f) => fd.append("files", f));

    try {
      await api.post("/inquiries", fd);
      setState("success");
      setForm(empty);
      setFiles([]);
      onClearMotif();
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
      setState("idle");
    }
  };

  if (state === "success") {
    return (
      <section id="anfrage" data-testid="inquiry-success" className="relative border-b border-obsidian-line bg-ink py-16 sm:py-24">
        <div className="ap-container max-w-2xl text-center">
          <div className="ap-illu mx-auto h-24 w-24 invert hue-rotate-180" aria-hidden="true">
            <Illu name={texts.illu_process_2} fallback="response" className="h-full w-full" />
          </div>
          <h2 className="ap-h2 mt-6 text-paper">{texts.inquiry_success_h}</h2>
          <p className="mt-5 text-base leading-relaxed text-paper/80 sm:text-lg">{texts.inquiry_success_body}</p>
          <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row">
            <a href="#arbeiten" className="ap-btn-invert" data-testid="success-works">
              Ausgewählte Arbeiten <ArrowRight size={15} />
            </a>
            <a
              href={instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="ap-btn-invert"
              data-testid="success-instagram"
            >
              <Instagram size={15} /> Instagram
            </a>
          </div>
          <button
            type="button"
            onClick={() => setState("idle")}
            className="ap-stamp mt-10 underline transition-colors hover:text-signal"
            data-testid="success-new-inquiry"
          >
            Weitere Anfrage senden
          </button>
        </div>
      </section>
    );
  }

  return (
    <section id="anfrage" data-testid="inquiry-section" className="relative border-b border-paper-line bg-paper-surface/60 ap-section">
      <div className="ap-container grid gap-10 lg:grid-cols-[0.74fr_1.26fr] lg:gap-14">
        <div>
          <div className="flex items-start justify-between gap-4">
            <SectionHead stamp="AP · ANFRAGE" title={texts.inquiry_h2} intro={texts.inquiry_help} />
            <div className="ap-illu h-20 w-20 shrink-0 sm:h-24 sm:w-24" aria-hidden="true">
              <Illu name={texts.illu_process_1} fallback="document" className="h-full w-full" />
            </div>
          </div>
          <p className="ap-stamp mt-8 leading-relaxed">
            Unverbindlich · Antwort in 2–4 Werktagen · Persönlich von René
          </p>
        </div>

        <div className="ap-fragment-frame" data-testid="inquiry-frame">
          <form onSubmit={submit} noValidate data-testid="inquiry-form" className="ap-quiet p-6 sm:p-8">
          {selectedMotif ? (
            <div
              data-testid="selected-motif-banner"
              className="mb-6 flex items-start justify-between gap-4 border border-signal bg-signal/5 p-4"
            >
              <div>
                <p className="ap-stamp text-signal">Ausgewähltes Motiv</p>
                <p className="mt-1 font-display text-base font-bold uppercase tracking-wide text-ink">
                  {selectedMotif.title} · {selectedMotif.code}
                </p>
              </div>
              <button
                type="button"
                onClick={onClearMotif}
                aria-label="Motivauswahl entfernen"
                data-testid="clear-motif"
                className="shrink-0 text-charcoal transition-colors hover:text-signal"
              >
                <X size={18} />
              </button>
            </div>
          ) : null}

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className="ap-label" htmlFor="if-name">Vor- und Nachname *</label>
              <input id="if-name" ref={nameRef} required value={form.name} onChange={set("name")} className="ap-field" data-testid="input-name" autoComplete="name" />
            </div>
            <div>
              <label className="ap-label" htmlFor="if-email">E-Mail *</label>
              <input id="if-email" type="email" required value={form.email} onChange={set("email")} className="ap-field" data-testid="input-email" autoComplete="email" />
            </div>
            <div>
              <label className="ap-label" htmlFor="if-phone">Telefonnummer (optional)</label>
              <input id="if-phone" type="tel" value={form.phone} onChange={set("phone")} className="ap-field" data-testid="input-phone" autoComplete="tel" />
            </div>
            <div>
              <label className="ap-label" htmlFor="if-channel">Bevorzugter Kontaktweg</label>
              <select id="if-channel" value={form.contact_channel} onChange={set("contact_channel")} className="ap-field" data-testid="input-channel">
                {CHANNELS.map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="ap-label" htmlFor="if-type">Projektart</label>
              <select id="if-type" value={form.project_type} onChange={set("project_type")} className="ap-field" data-testid="input-type">
                {TYPES.map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="ap-label" htmlFor="if-desc">Kurze Beschreibung deiner Idee *</label>
              <textarea id="if-desc" required rows={5} value={form.description} onChange={set("description")} className="ap-field resize-y" data-testid="input-description" />
            </div>
            <div>
              <label className="ap-label" htmlFor="if-place">Körperstelle</label>
              <input id="if-place" value={form.placement} onChange={set("placement")} className="ap-field" data-testid="input-placement" />
            </div>
            <div>
              <label className="ap-label" htmlFor="if-size">Ungefähre Größe (optional)</label>
              <input id="if-size" value={form.size} onChange={set("size")} className="ap-field" data-testid="input-size" placeholder="z. B. 20 × 15 cm" />
            </div>
            <div className="sm:col-span-2">
              <label className="ap-label" htmlFor="if-files">Referenzbilder (optional, max. 3)</label>
              <input
                id="if-files"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic"
                multiple
                onChange={(e) => setFiles(Array.from(e.target.files).slice(0, 3))}
                className="ap-field file:mr-3 file:border-0 file:bg-ink file:px-3 file:py-2 file:font-mono file:text-[11px] file:uppercase file:tracking-wider file:text-paper"
                data-testid="input-files"
              />
              {files.length ? (
                <p className="ap-stamp mt-2" data-testid="files-count">{files.length} Datei(en) ausgewählt</p>
              ) : null}
            </div>
          </div>

          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={form.honeypot}
            onChange={set("honeypot")}
            aria-hidden="true"
            className="absolute h-0 w-0 overflow-hidden opacity-0"
            data-testid="input-honeypot"
          />

          <div className="mt-8 space-y-4 border-t border-paper-line pt-6">
            <label className="flex cursor-pointer items-start gap-3 text-sm text-charcoal">
              <input type="checkbox" required checked={form.consent} onChange={set("consent")} className="mt-1 h-5 w-5 shrink-0 accent-signal" data-testid="input-consent" />
              <span>
                {texts.inquiry_consent}{" "}
                <Link to="/datenschutz" className="underline hover:text-signal">Datenschutzerklärung</Link>
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-3 text-sm text-charcoal">
              <input type="checkbox" checked={form.marketing_consent} onChange={set("marketing_consent")} className="mt-1 h-5 w-5 shrink-0 accent-signal" data-testid="input-marketing" />
              <span>{texts.inquiry_marketing}</span>
            </label>
          </div>

          {error ? (
            <p role="alert" data-testid="inquiry-error" className="mt-6 border border-destructive bg-destructive/5 p-4 text-sm text-destructive">
              {error}
            </p>
          ) : null}

          <button type="submit" disabled={state === "sending"} data-testid="inquiry-submit" className="ap-btn-solid mt-8 w-full disabled:opacity-60">
            {state === "sending" ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {texts.inquiry_button}
          </button>
          </form>
        </div>
      </div>
    </section>
  );
};
